import { AppContext } from 'contexts/app-context';
import { useLanguage } from 'i18n/LanguageProvider';
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';

import { PaginatedDropdown } from 'components/ui/paginated-dropdown';
import { allFilterOption } from 'utils/filter-options';

interface PaginatedHookParams {
  accountSlug: string;
  brandId: string;
  query: string;
  perPage: number;
  locale?: string;
}

interface PaginatedHookResponse<T> {
  data: T[];
  isFetching: boolean;
  hasMore: boolean;
  loadMore: () => void;
  addPositionToCache?: (position: { id: string; name: string }) => void;
}

interface PaginatedItem {
  id: string;
  name: string;
}

interface PaginatedFilterProps<T extends PaginatedItem> {
  accountSlug: string;
  brandId: string;
  label: string;
  type: string;
  activeLabel: string;
  useHook: (params: PaginatedHookParams) => PaginatedHookResponse<T>;
  onSelect: (type: string, option: { key: string; label: string }) => void;
  includeAllOption?: boolean;
  perPage?: number;
}

export function PaginatedFilter<T extends PaginatedItem>({
  accountSlug,
  brandId,
  label,
  type,
  activeLabel,
  useHook,
  onSelect,
  includeAllOption = true,
  perPage = 50,
}: PaginatedFilterProps<T>) {
  const intl = useIntl();
  const [searchQuery, setSearchQuery] = useState('');
  const { currentLanguage: locale } = useLanguage();
  const { position, setActiveFilters, activeFilters } = useContext(AppContext);
  const { data, isFetching, hasMore, loadMore, addPositionToCache } = useHook({
    accountSlug,
    brandId,
    query: searchQuery,
    perPage,
    locale,
  });

  const options = [
    ...(includeAllOption ? [allFilterOption(intl)] : []),
    ...data.map(item => ({
      key: type === 'category' ? item.name : item.id,
      label: item.name,
    })),
  ];

  // Update activeFilters.position when position data is loaded and matches current position ID
  // Use a ref to track if we've already updated to prevent infinite loops
  const hasUpdatedPositionRef = useRef(false);

  useEffect(() => {
    // For position filters, only update if there's a genuine mismatch
    // This prevents double calls when handleFilterChange already updated both states
    if (
      type === 'position' &&
      position.id &&
      data.length > 0 &&
      activeFilters.position.key !== position.id &&
      !hasUpdatedPositionRef.current
    ) {
      const matchingPosition = data.find(item => item.id === position.id);
      if (matchingPosition) {
        // Double-check right before updating to prevent race conditions
        if (activeFilters.position.key !== position.id) {
          setActiveFilters(prev => ({
            ...prev,
            position: {
              key: position.id as string,
              label: matchingPosition.name,
            },
          }));
          hasUpdatedPositionRef.current = true;
        }
      }
    }
  }, [data, position.id, activeFilters.position.key, setActiveFilters, type]);

  // Reset the ref when position.id changes
  useEffect(() => {
    hasUpdatedPositionRef.current = false;
  }, [position.id]);

  // Check for position in global cache when position.id changes
  useEffect(() => {
    if (
      type === 'position' &&
      position.id &&
      position.value &&
      addPositionToCache
    ) {
      // Check if position is already in current data
      const matchingPosition = data.find(item => item.id === position.id);
      if (!matchingPosition) {
        // Position not found in current data, add it to cache and data
        addPositionToCache({ id: position.id, name: position.value });
      }
    }
  }, [position.id, position.value, data, type, addPositionToCache]);

  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  return (
    <PaginatedDropdown
      label={label}
      type={type}
      value={activeLabel}
      options={options}
      onSelect={onSelect}
      onSearch={handleSearch}
      onLoadMore={loadMore}
      hasMore={hasMore}
      isLoading={isFetching}
      searchable
    />
  );
}

export type { PaginatedHookParams, PaginatedHookResponse, PaginatedItem };
